import { useEffect, useRef } from 'react';

/**
 * Calls `refresh` once each time the window comes back after being left: the
 * tab was hidden or the window lost focus, then it was shown or focused again.
 * A tab switch fires both a visibility change and a focus event, and only the
 * first return counts. `refresh` is read when the return happens, so a new
 * function each render does not re-arm the listeners.
 */
export default function useRefreshOnReturn(refresh, enabled = true) {
    const refreshRef = useRef(refresh);
    refreshRef.current = refresh;

    useEffect(() => {
        if (!enabled) return undefined;
        let away = false;
        const leave = () => {
            away = true;
        };
        const back = () => {
            if (!away || document.visibilityState === 'hidden') return;
            away = false;
            refreshRef.current();
        };
        const onVisibility = () =>
            document.visibilityState === 'hidden' ? leave() : back();
        window.addEventListener('blur', leave);
        window.addEventListener('focus', back);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('blur', leave);
            window.removeEventListener('focus', back);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [enabled]);
}
